export type Verdict = "human_typical" | "mixed_signals" | "ai_typical";

export type AuthorshipLabel = "human_written" | "ai_edited" | "ai_generated";
export type Confidence = "low" | "medium" | "high";
export type OperatingPoint = "0.01%" | "0.1%";
export const labelCopy: Record<
  AuthorshipLabel,
  { name: string; note: string }
> = {
  human_written: {
    name: "Human written",
    note: "The classifier found no material AI contribution in this passage.",
  },
  ai_edited: {
    name: "AI edited",
    note: "The classifier found retained human authorship with an AI contribution. This does not estimate what percentage AI wrote.",
  },
  ai_generated: {
    name: "AI generated",
    note: "The classifier found a fully machine-derived passage.",
  },
};
export const confidenceExplanation =
  "Relative KNN support from neighbor agreement, proximity, and decision margin compared with calibration examples. This is not a probability of correctness.";
export const PUBLIC_RESPONSE_VERSION = "stylome-public-v2";
export interface ScoreResult {
  label: AuthorshipLabel;
  confidence: Confidence;
  id: string;
  mock: boolean;
  content_hash: string;
  model_version: string;
  created_at: string;
  operating_point: OperatingPoint;
  response_version: string;
  evaluation_version: string | null;
  receipt: { id: string; share_token: string; public_share: false };
}

export function parseScoreResult(body: unknown): ScoreResult {
  if (!body || typeof body !== "object")
    throw new Error("The scanner returned an invalid result.");
  const value = body as Record<string, unknown>;
  const receipt = value.receipt as Record<string, unknown> | undefined;
  if (
    !["human_written", "ai_edited", "ai_generated"].includes(
      String(value.label),
    ) ||
    !["low", "medium", "high"].includes(String(value.confidence)) ||
    value.model_version !== "stylome-1.2-large" ||
    value.operating_point !== "0.01%" ||
    value.response_version !== PUBLIC_RESPONSE_VERSION ||
    typeof value.evaluation_version !== "string" ||
    typeof value.id !== "string" ||
    typeof value.mock !== "boolean" ||
    typeof value.created_at !== "string" ||
    typeof value.content_hash !== "string" ||
    typeof receipt?.id !== "string" ||
    typeof receipt.share_token !== "string" ||
    receipt.public_share !== false
  )
    throw new Error("The scanner returned an invalid result.");
  return {
    id: value.id,
    label: value.label as AuthorshipLabel,
    confidence: value.confidence as Confidence,
    mock: value.mock,
    model_version: value.model_version,
    created_at: value.created_at,
    content_hash: value.content_hash,
    operating_point: value.operating_point,
    response_version: value.response_version,
    evaluation_version: value.evaluation_version,
    receipt: {
      id: receipt.id,
      share_token: receipt.share_token,
      public_share: false,
    },
  };
}

export const examples = {
  human:
    "I found the receipt folded inside the old field guide, next to a coffee stain shaped like Michigan. The handwriting was my grandfather's, cramped and practical, listing nails, lamp oil, and three pounds of apples. Nothing about it was important, which is probably why I kept reading it. Ordinary details have a way of surviving the people who wrote them.",
  mixed:
    "The committee reviewed the proposal and identified several promising elements. However, the timeline remains ambitious, and the available evidence does not yet support every projected outcome. A revised plan should distinguish between work that can begin immediately and decisions that depend on the pilot results.",
};

export function normalizeText(value: string) {
  return value
    .replace(
      // Python str.split whitespace includes these control separators.
      // eslint-disable-next-line no-control-regex
      /[\u0009-\u000d\u001c-\u0020\u0085\u00a0\u1680\u2000-\u200a\u2028\u2029\u202f\u205f\u3000]+/gu,
      " ",
    )
    .replace(/^ +| +$/g, "");
}

export async function scoreText(
  text: string,
  turnstileToken: string | null,
  signal?: AbortSignal,
): Promise<ScoreResult> {
  const response = await fetch("/api/demo/v1/score", {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify({ text, turnstile_token: turnstileToken }),
    signal,
  });
  const body = await response.json().catch(() => null);
  if (!response.ok)
    throw new Error(
      body?.error?.message ||
        body?.detail ||
        "The scanner did not return a result.",
    );
  return parseScoreResult(body);
}
